// Travel Partners SSO Integration Script
//
// Rebuilt 2026-08-25 on the Flow 3 member-token flow. The old chain — scrape
// window.sessionId, fetch a raw member token from the backend into the
// BROWSER, call the gateway from the browser — is gone. One call to the app
// via Shopify's signed App Proxy now does everything server-side (identity
// from the signed logged_in_customer_id, token minted from the stored grant,
// warm-up retry, destination allow-list) and returns only the one-shot
// travel login link.
document.addEventListener("DOMContentLoaded", function () {
  injectSpinnerStyles();

  // Small delay to ensure DOM is fully loaded
  setTimeout(() => {
    const buttons = document.querySelectorAll('.travel-button');
    buttons.forEach(button => {
      button.addEventListener("click", handleTravelClick);
    });
  }, 500);

  // A travel click that needed the invisible sign-in resumes here after the
  // bounce — the member clicked once, so the click carries through.
  try {
    if (sessionStorage.getItem('mmTravelResume')) {
      sessionStorage.removeItem('mmTravelResume');
      showLoadingSpinner("Logging you in...");
      requestTravelLogin(true, false);
    }
  } catch (e) { /* sessionStorage unavailable */ }
});

// One client-side retry for transient failures (the server already handles
// the gateway's first-call-403 warm-up with its own paced retry).
const CLIENT_RETRY_DELAY_MS = 1500;

// =============================
// Spinner helpers
// =============================
function injectSpinnerStyles() {
  if (document.getElementById('sso-spinner-styles')) return;
  const styles = `
    <style id="sso-spinner-styles">
      .sso-loading-overlay {
        position: fixed; top: 0; left: 0; width: 100%; height: 100%;
        background: rgba(0, 0, 0, 0.5); display: flex; justify-content: center; align-items: center;
        z-index: 9999; opacity: 0; visibility: hidden;
        transition: opacity 0.3s ease, visibility 0.3s ease;
      }
      .sso-loading-overlay.show { opacity: 1; visibility: visible; }
      .sso-spinner-container { background: white; padding: 30px; border-radius: 10px;
        text-align: center; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15); max-width: 300px; }
      .sso-spinner { width: 40px; height: 40px; border: 4px solid #f3f3f3; border-top: 4px solid #007bff;
        border-radius: 50%; animation: sso-spin 1s linear infinite; margin: 0 auto 15px; }
      @keyframes sso-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      .sso-loading-text { color: #333; font-size: 16px; margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
    </style>
  `;
  document.head.insertAdjacentHTML('beforeend', styles);
}

function showLoadingSpinner(message = "Redirecting to Travel Partners...") {
  let overlay = document.getElementById('sso-loading-overlay');
  if (!overlay) {
    const spinnerHTML = `
      <div id="sso-loading-overlay" class="sso-loading-overlay">
        <div class="sso-spinner-container">
          <div class="sso-spinner"></div>
          <p class="sso-loading-text">${message}</p>
        </div>
      </div>
    `;
    document.body.insertAdjacentHTML('beforeend', spinnerHTML);
    overlay = document.getElementById('sso-loading-overlay');
    setTimeout(() => overlay.classList.add('show'), 10);
  } else {
    const textEl = overlay.querySelector('.sso-loading-text');
    if (textEl) textEl.textContent = message;
    overlay.classList.add('show');
  }
}

function hideLoadingSpinner() {
  const overlay = document.getElementById('sso-loading-overlay');
  if (overlay) {
    overlay.classList.remove('show');
    setTimeout(() => overlay.remove(), 300);
  }
}

// =============================
// WebView detection
// =============================
function isWebView() {
  const ua = navigator.userAgent || navigator.vendor || window.opera;

  const isIOSWebView = /iP(ad|hone|od)/.test(ua) && !/(Safari|CriOS|FxiOS|EdgiOS)/i.test(ua);
  const isAndroidWebView = /Android/.test(ua) && (/wv|Version\/\d+\.\d+/.test(ua) && !/Chrome\/[.0-9]*/.test(ua) || /; wv\)/.test(ua));

  const hasWebViewIndicators =
    (window.ReactNativeWebView !== undefined) ||
    (window.flutter_inappwebview !== undefined) ||
    (window.webkit?.messageHandlers !== undefined) ||
    (typeof AndroidInterface !== 'undefined');

  return isIOSWebView || isAndroidWebView || hasWebViewIndicators;
}

// =============================
// Click handler
// =============================
function handleTravelClick(event) {
  // Logged-out visitors must never enter the SSO chain (no session can exist —
  // they got the spinner + "Session not ready" alert; Dirk, PROD, 4 Sep).
  // Send them to the travel desk page to log in themselves. mmLoggedIn is
  // server-rendered truth from layout/theme.liquid.
  if (window.mmLoggedIn !== true) {
    event.preventDefault();
    window.location.href = "https://www.multiply.co.za/engaged/flyoverTravelDesk";
    return;
  }

  event.preventDefault();

  showLoadingSpinner("Logging you in...");
  requestTravelLogin(true, true);
}

// =============================
// The one call that replaces the old chain. The app answers with either the
// one-shot travel link, or (first visit since the new flow) a sign-in URL for
// an invisible round trip — the click intent is saved so travel resumes and
// opens by itself when the member lands back.
// mayBounce is false on that resumed attempt: if the grant STILL isn't there
// after a successful bounce, the mall login and the Multiply login are
// different people (or something upstream is wrong) and bouncing again would
// loop forever — stop and say so instead.
// =============================
function requestTravelLogin(mayRetry, mayBounce) {
  const path = window.location.pathname + window.location.search;

  fetch('/apps/multiply/travel?path=' + encodeURIComponent(path), { credentials: 'same-origin' })
    .then(res => res.json().then(data => ({ status: res.status, data })))
    .then(({ status, data }) => {
      if (data && data.ok && data.url) {
        hideLoadingSpinner();
        openRedirect(data.url);
        return;
      }
      if (data && data.grant === 'needed' && data.authUrl) {
        var silentAuthFailed = /[?&]mm_auth=none(&|$)/.test(window.location.search);
        if (!mayBounce || silentAuthFailed) {
          hideLoadingSpinner();
          alert("⚠️ We couldn't confirm your Multiply membership. Please log out of the mall, sign in again, and retry Travel.");
          return;
        }
        try { sessionStorage.setItem('mmTravelResume', '1'); } catch (e) { /* resume unavailable */ }
        window.location.href = data.authUrl;
        return;
      }
      if (status === 401) {
        hideLoadingSpinner();
        alert("⚠️ Please log in to the mall first, then try again.");
        return;
      }
      if (status === 429) {
        hideLoadingSpinner();
        alert("⚠️ Too many attempts. Please wait a minute and try again.");
        return;
      }
      throw new Error('HTTP ' + status);
    })
    .catch(err => {
      if (mayRetry) {
        setTimeout(() => requestTravelLogin(false, mayBounce), CLIENT_RETRY_DELAY_MS);
        return;
      }
      hideLoadingSpinner();
      alert("⚠️ Could not log in, please try again.");
      throw err;
    });
}

// Opens the travel desk. Same-tab inside webviews; a new tab on the web, with a
// same-tab fallback if the popup is blocked (the retry path can open outside the
// original click gesture).
function openRedirect(redirectUrl) {
  if (isWebView()) {
    window.location.href = redirectUrl;
    return;
  }
  const newWindow = window.open(redirectUrl, "_blank");
  if (!newWindow) {
    window.location.href = redirectUrl;
  }
}

// =============================
// Cleanup on back navigation
// =============================
function cleanupOnReturn() {
  const overlay = document.getElementById('sso-loading-overlay');
  if (overlay) {
    overlay.remove();
  }

  const nativeSpinnerSelectors = [
    '.loading-spinner',
    '.native-spinner',
    '.webview-loading',
    '[class*="spinner"]',
    '[class*="loading"]',
    '[id*="spinner"]',
    '[id*="loading"]'
  ];

  nativeSpinnerSelectors.forEach(selector => {
    document.querySelectorAll(selector).forEach(el => {
      const style = window.getComputedStyle(el);
      if (style.position === 'fixed' || style.position === 'absolute') {
        if (el.offsetWidth < 200 && el.offsetHeight < 200) {
          el.remove();
        }
      }
    });
  });
}

window.addEventListener('pageshow', cleanupOnReturn);

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    cleanupOnReturn();
  }
});
